import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { isPublicPortalAuthUser } from '../utils/accessControl';
import {
  getAllCalendarEvents,
  addCalendarEvent as addCalendarEventService,
  updateCalendarEvent as updateCalendarEventService,
  deleteCalendarEvent as deleteCalendarEventService
} from '../firebase/calendarService';

const CalendarEventsContext = createContext(null);

/**
 * Shared calendar events state used by both the Calendar and Inspections pages.
 * Any mutation (add / update / delete) triggers a single re-fetch so every
 * consumer (calendar grid, inspections "Booked visit" column, upcoming timeline)
 * updates immediately without manual refresh.
 */
const calendarRouteActive = (pathname) =>
  pathname === '/' || pathname === '/dashboard' || pathname.startsWith('/inspections');

export const CalendarEventsProvider = ({ children }) => {
  const { currentUser, userRole } = useAuth();
  const { pathname } = useLocation();
  const active = calendarRouteActive(pathname);
  const staffUser = currentUser && !isPublicPortalAuthUser(currentUser, userRole);
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);

  const refreshEvents = useCallback(async () => {
    if (!staffUser?.uid) return;
    try {
      const data = await getAllCalendarEvents();
      setEvents(Array.isArray(data) ? data : []);
    } catch (err) {
      console.warn('[CalendarEvents] refresh failed:', err);
    }
  }, [staffUser?.uid]);

  // ponytail: only Dashboard and Inspections read events — skip the full calendar download on every other route
  useEffect(() => {
    if (!staffUser?.uid || !active) return;
    setEventsLoading(true);
    refreshEvents().finally(() => setEventsLoading(false));
  }, [staffUser?.uid, active, refreshEvents]);

  useEffect(() => {
    if (!active) return;
    const onVisibility = () => {
      if (document.visibilityState === 'visible') refreshEvents();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [active, refreshEvents]);

  const addEvent = useCallback(async (eventData) => {
    const id = await addCalendarEventService(eventData);
    await refreshEvents();
    return id;
  }, [refreshEvents]);

  const updateEvent = useCallback(async (eventId, eventData) => {
    await updateCalendarEventService(eventId, eventData);
    await refreshEvents();
  }, [refreshEvents]);

  const deleteEvent = useCallback(async (eventId) => {
    const result = await deleteCalendarEventService(eventId);
    await refreshEvents();
    return result;
  }, [refreshEvents]);

  return (
    <CalendarEventsContext.Provider
      value={{ events, eventsLoading, refreshEvents, addEvent, updateEvent, deleteEvent }}
    >
      {children}
    </CalendarEventsContext.Provider>
  );
};

export const useCalendarEvents = () => {
  const ctx = useContext(CalendarEventsContext);
  if (!ctx) throw new Error('useCalendarEvents must be used inside <CalendarEventsProvider>');
  return ctx;
};
